import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import UtilsTable from '../../components/farm-util/UtilsTable';
import {
  loadTaxonomyGroups,
  loadUtilsData,
} from '../../store/utils/utils.actions';
import TaxonomyTable from '../../components/farm-util/TaxonomyTable';
import { selectUtils } from '../../store/utils/utils.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { IUtilData } from '../../entities/utils.entities';
import { Button, Modal, Table } from 'antd';
import {
  CheckboxButton,
  CloseIcon,
  Dropdown,
  Input,
  Subtitle,
  Title,
} from '../../components/shared';
import { sendSingleRequest } from '../../apis';
import { showFeedback } from '../../store/ui/ui.actions';
import ModalFeedbackView from '../../components/shared/feedback/ModalFeedbackView';
import { selectAccount } from '../../store/auth/auth.selector';
import { updateAccountSetting } from '../../store/auth/auth.actions';

const SEED_TYPE_OPTIONS: {
  value: IUtilData['type'];
  label: string;
}[] = [
  { value: 'mussel_seed_type', label: 'Mussel' },
  { value: 'oyster_seed_type', label: 'Oyster' },
  { value: 'seaweed_seed_type', label: 'Seaweed' },
];

const seedTypeCropLabel = (type: IUtilData['type']) =>
  SEED_TYPE_OPTIONS.find(o => o.value === type)?.label ?? type;

const SeedTypeTable = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmUtils = useSelector(selectUtils);
  const seedTypes = farmUtils.filter(x =>
    SEED_TYPE_OPTIONS.some(o => o.value === x.type),
  );

  const [disabled, setDisabled] = useState(false);
  const [formItem, setFormItem] = useState<{
    id: number;
    name: string;
    type: IUtilData['type'];
  } | null>(null);

  const showError = (message: string) =>
    dispatch(showFeedback({ type: 'error', message, isMessage: true }));

  const openAdd = () =>
    setFormItem({ id: 0, name: '', type: 'mussel_seed_type' });

  const openEdit = (x: IUtilData) =>
    setFormItem({ id: x.id, name: x.name, type: x.type });

  const confirmDelete = async (x: IUtilData) => {
    if (
      !window.confirm(
        translate(lang, 'Are you sure you want to delete this item?'),
      )
    )
      return;
    setDisabled(true);
    const res = await sendSingleRequest(
      {},
      'DELETE',
      `api/utils/${x.id}`,
      true,
    );
    if (res.status) {
      await dispatch(loadUtilsData());
    } else {
      showError(res.data?.message ?? 'Unknown error');
    }
    setDisabled(false);
  };

  const confirmSave = async () => {
    if (!formItem) return;
    if (!formItem.name.trim()) {
      showError(translate(lang, 'Name is required'));
      return;
    }
    setDisabled(true);
    const res =
      formItem.id === 0
        ? await sendSingleRequest(
            { name: formItem.name, type: formItem.type },
            'POST',
            'api/utils',
            true,
          )
        : await sendSingleRequest(
            { name: formItem.name, type: formItem.type },
            'PUT',
            `api/utils/${formItem.id}`,
            true,
          );
    if (res.status) {
      await dispatch(loadUtilsData());
      setFormItem(null);
    } else {
      showError(res.data?.message ?? 'Unknown error');
    }
    setDisabled(false);
  };

  const columns = [
    {
      title: translate(lang, 'Name'),
      key: 'name',
      render: (x: IUtilData) => x.name,
    },
    {
      title: translate(lang, 'Crop type'),
      key: 'crop_type',
      render: (x: IUtilData) => seedTypeCropLabel(x.type),
    },
    {
      title: '',
      key: 'action',
      align: 'right' as const,
      render: (x: IUtilData) => (
        <div className='d-flex justify-end gap-8'>
          <Button size='small' onClick={() => openEdit(x)} disabled={disabled}>
            {translate(lang, 'Edit')}
          </Button>
          <Button
            size='small'
            type='primary'
            danger
            onClick={() => confirmDelete(x)}
            disabled={disabled}
          >
            {translate(lang, 'Delete')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className='mb-32'>
      <div className='d-flex align-end justify-between'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, 'Seed Type List')}
        </Title>
        <div className='mr-32'>
          <Button type='primary' onClick={openAdd} disabled={disabled}>
            {translate(lang, 'Add Seed Type')}
          </Button>
        </div>
      </div>
      <Table
        rowKey='id'
        className='table table--units'
        pagination={false}
        columns={columns}
        dataSource={seedTypes}
      />
      {formItem && (
        <Modal
          visible={true}
          onCancel={() => setFormItem(null)}
          footer={null}
          closable
          closeIcon={<CloseIcon />}
          width={500}
        >
          <div className='wrap'>
            <div className='d-flex align-center mb-24'>
              <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
                {translate(
                  lang,
                  formItem.id === 0 ? 'Add Seed Type' : 'Edit Seed Type',
                )}
              </Subtitle>
            </div>
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Name')}
                type='text'
                value={formItem.name}
                onChange={e =>
                  setFormItem({ ...formItem, name: e.target.value })
                }
                disabled={disabled}
                required
              />
            </div>
            <div className='mb-17'>
              <Dropdown
                label={translate(lang, 'Crop type')}
                value={formItem.type}
                options={SEED_TYPE_OPTIONS.map(o => ({
                  id: o.value,
                  value: o.value,
                  label: translate(lang, o.label),
                }))}
                onChange={v =>
                  setFormItem({
                    ...formItem,
                    type: v as IUtilData['type'],
                  })
                }
              />
            </div>
          </div>
          <div className='d-flex justify-end mt-24'>
            <Button
              type='primary'
              size='large'
              disabled={disabled}
              onClick={confirmSave}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
          <ModalFeedbackView />
        </Modal>
      )}
    </div>
  );
};

const CommentTemplates = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmUtils = useSelector(selectUtils);
  const templates = farmUtils.filter(x => x.type === 'comment_template');

  const [comment, setComment] = useState<string>();
  const [disabled, setDisabled] = useState(false);

  const confirmAdd = async () => {
    if (!comment || comment.trim().length <= 0) {
      dispatch(
        showFeedback({
          isMessageModal: true,
          type: 'error',
          message: translate(lang, 'Comment cannot be empty'),
        }),
      );
      return;
    }
    setDisabled(true);
    const res = await sendSingleRequest(
      {
        name: comment,
        type: 'comment_template',
      },
      'POST',
      'api/utils',
      true,
    );
    if (res.status) {
      await dispatch(loadUtilsData());
      setComment(undefined);
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: res.data?.message ?? 'Unknown error',
        }),
      );
    }
    setDisabled(false);
  };
  const deleteItem = async (id: number) => {
    if (
      !window.confirm(
        translate(lang, 'Are you sure you want to delete this item?'),
      )
    )
      return;
    setDisabled(true);
    const res = await sendSingleRequest({}, 'DELETE', `api/utils/${id}`, true);
    if (res.status) {
      await dispatch(loadUtilsData());
    } else {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: res.data?.message ?? 'Unknown error',
        }),
      );
    }
    setDisabled(false);
  };

  const columns = [
    {
      title: translate(lang, 'Content'),
      key: 'name',
      render: (x: IUtilData) => (
        <span style={{ whiteSpace: 'pre-wrap' }}>{x.name}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (x: IUtilData) => (
        <Button
          type='primary'
          size='small'
          danger={true}
          onClick={() => deleteItem(x.id)}
          disabled={disabled}
        >
          {translate(lang, 'Delete')}
        </Button>
      ),
    },
  ];

  return (
    <div className='mb-32'>
      <div className='d-flex align-end justify-between'>
        <Title size={6} color='black-3' align='default' fontWeight={500}>
          {translate(lang, 'Comment templates')}
        </Title>
        <div className='mr-32'>
          <Button
            type='primary'
            disabled={disabled}
            onClick={() => setComment('')}
          >
            {translate(lang, 'Add template')}
          </Button>
        </div>
      </div>
      <Table
        rowKey='id'
        className='table table--isFarms'
        pagination={false}
        columns={columns}
        dataSource={templates}
      />
      {comment !== undefined && (
        <Modal
          visible={true}
          onCancel={() => setComment(undefined)}
          footer={null}
          closable
          closeIcon={<CloseIcon />}
          width={650}
        >
          <div className='wrap'>
            <div className='d-flex align-center mb-24'>
              <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
                {translate(lang, 'Add a comment template')}
              </Subtitle>
            </div>
            <div className='mb-17'>
              <Input
                label={translate(lang, 'Comment')}
                placeholder={translate(lang, 'Write your text here')}
                type='textarea'
                onChange={e => setComment(e.target.value)}
                value={comment}
                disabled={disabled}
                max={255}
              />
            </div>
          </div>
          <div className='d-flex justify-end mt-24'>
            <Button
              type='primary'
              color='green'
              size='large'
              disabled={disabled}
              onClick={confirmAdd}
            >
              {translate(lang, 'Confirm')}
            </Button>
          </div>
          <ModalFeedbackView />
        </Modal>
      )}
    </div>
  );
};

const StaticFieldsSetting = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farmUtils = useSelector(selectUtils);
  const ports = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivers = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse'),
    seasons = farmUtils.filter(x => x.type === 'season'),
    bioTypes = farmUtils.filter(x => x.type === 'biosecurity_type'),
    farmingMethods = farmUtils.filter(x => x.type === 'farming_method'),
    expenseItems = farmUtils.filter(x => x.type === 'expense_item'),
    customers = farmUtils.filter(x => x.type === 'customer'),
    maintenances = farmUtils.filter(x => x.type === 'maintenance');
  const haveCustomers = useSelector(selectAccount)?.have_customers;

  const [disabled, setDisabled] = useState(false);

  const toggleHaveCustomers = async (c: boolean) => {
    setDisabled(true);
    await dispatch(updateAccountSetting({ have_customers: c }));
    setDisabled(false);
  };

  useEffect(() => {
    dispatch(loadUtilsData());
    dispatch(loadTaxonomyGroups());
  }, [dispatch]);

  return (
    <div className='content pb-32'>
      <div className='mb-32'>
        <UtilsTable
          data={seasons}
          type='season'
          label={`${translate(lang, 'Season')} ${translate(lang, 'options')}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Season',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <TaxonomyTable />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={maintenances}
          type='maintenance'
          label='Maintenance List'
        />
      </div>
      <div className='mb-32'>
        <SeedTypeTable />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={ports}
          type='port_of_unload'
          label={`${translate(lang, 'Port of unload')} ${translate(
            lang,
            'options',
          )}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Port of unload',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={delivers}
          type='delivered_to'
          label={`${translate(lang, 'Delivered to')} ${translate(
            lang,
            'options',
          )}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Delivered to',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={packhouses}
          type='packhouse'
          label={`${translate(lang, 'Packhouse')} ${translate(
            lang,
            'options',
          )}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Packhouse',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={bioTypes}
          type='biosecurity_type'
          label={`${translate(lang, 'Biosecurity type')} ${translate(
            lang,
            'options',
          )}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Biosecurity type',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={farmingMethods}
          type='farming_method'
          label={`${translate(lang, 'Farming method')} ${translate(
            lang,
            'options',
          )}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'Farming method',
          )} ${translate(lang, 'Option')}`}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={expenseItems}
          type='expense_item'
          label={`${translate(lang, 'Expense')} ${translate(lang, 'options')}`}
          btnLabel={`${translate(lang, 'Add')} ${translate(
            lang,
            'expense',
          )} ${translate(lang, 'Option')}`}
          numLabel={translate(lang, 'Unit price')}
          numUnit='$'
        />
      </div>
      <div className='mb-32'>
        <CheckboxButton
          label={translate(lang, 'I have customers')}
          checked={haveCustomers}
          onChange={e => toggleHaveCustomers(e.target.checked)}
          disabled={disabled}
        />
      </div>
      <div className='mb-32'>
        <UtilsTable
          data={customers}
          label={translate(lang, 'Customer')}
          type='customer'
          flagLabel={translate(lang, 'Internal Customer')}
          flagPlaceholder={translate(
            lang,
            '_internal_customer_most_cases',
          )}
        />
      </div>
      <CommentTemplates />
    </div>
  );
};

export default StaticFieldsSetting;
